import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import OutfitCard from "../components/Outfit/OutfitCard";
import HeroCarousel from "../components/Hero/HeroCarousel";
import OutfitOfTheDay from "../components/Social/OutfitOfTheDay";
import InFeedAdGrid from "../components/Ads/InFeedAdGrid";
import NativeInFeedAd from "../components/Ads/NativeInFeedAd";
import HeroIntro from "../components/Home/HeroIntro";
import HowItWorks from "../components/Home/HowItWorks";
import FeaturedArticles from "../components/Home/FeaturedArticles";
import PopularCategories from "../components/Home/PopularCategories";
import SEOHead from "../components/SEO/SEOHead";
import { Outfit } from "../types";
import OutfitService from "../services/outfit-service";
import { useAuth } from "../contexts/AuthContext";

const HomePage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [trendingOutfits, setTrendingOutfits] = useState<Outfit[]>([]);
  const [eventOutfits, setEventOutfits] = useState<Record<string, Outfit[]>>({});
  const [isLoadingTrending, setIsLoadingTrending] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [trendingError, setTrendingError] = useState<string | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        setIsLoadingTrending(true);
        setTrendingError(null);
        const outfits = await OutfitService.getTrending(12);
        setTrendingOutfits(outfits || []);
      } catch (error) {
        console.error("Error fetching trending:", error);
        setTrendingError("Failed to load trending outfits.");
      } finally {
        setIsLoadingTrending(false);
      }
    };
    if (!authLoading) fetchTrending();
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoadingEvents(true);
        setEventsError(null);
        const types = ["Casual", "Formal", "Party"];
        const map: Record<string, Outfit[]> = {};
        await Promise.all(
          types.map(async (t) => {
            try {
              map[t] = await OutfitService.getOutfitsByEventType(t, 3);
            } catch (e) {
              console.error("Error fetching " + t + ":", e);
            }
          })
        );
        setEventOutfits(map);
      } catch (error) {
        console.error("Error fetching events:", error);
        setEventsError("Failed to load event outfits.");
      } finally {
        setIsLoadingEvents(false);
      }
    };
    if (!authLoading) fetchEvents();
  }, [authLoading, isAuthenticated]);

  // Shuffle outfits for carousel — random order each page load
  const heroOutfits = useMemo(() => {
    const shuffled = [...trendingOutfits];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 5);
  }, [trendingOutfits]);

  // OOTD cycles daily — pick outfit based on day-of-year index into rating-sorted list
  const outfitOfTheDay = useMemo(() => {
    if (trendingOutfits.length === 0) return null;
    const sorted = [...trendingOutfits].sort(
      (a, b) => (b.ratings?.averageRating || 0) - (a.ratings?.averageRating || 0)
    );
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
    return sorted[dayOfYear % sorted.length];
  }, [trendingOutfits]);
  const justPosted = useMemo(() => {
    const cutoff = Date.now() - 2 * 60 * 60 * 1000;
    return trendingOutfits.filter((o) => new Date(o.createdAt).getTime() >= cutoff);
  }, [trendingOutfits]);

  return (
    <Box>
      <SEOHead
        title="Hooliah — Discover & Share Fashion"
        description="Upload your outfits, get AI-powered style analysis, and connect with a community of fashion enthusiasts on Hooliah."
        canonicalUrl="https://hooliah.com"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Hooliah',
          url: 'https://hooliah.com',
          description: 'AI-powered fashion community for outfit sharing, rating, and discovery.',
          potentialAction: { '@type': 'SearchAction', target: 'https://hooliah.com/search?q={search_term_string}', 'query-input': 'required name=search_term_string' },
        }}
      />

      <HeroIntro />
      <HeroCarousel outfits={heroOutfits} tagline="What's your fit today?" />

      {outfitOfTheDay && <OutfitOfTheDay outfit={outfitOfTheDay} />}
      {justPosted.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
            New Posts
          </Typography>
          <Grid container spacing={2}>
            {justPosted.slice(0, 4).map((outfit) => (
              <Grid item xs={6} sm={3} key={outfit.id}>
                <OutfitCard outfit={outfit} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>Trending Outfits</Typography>
          <Button component={RouterLink} to="/search?sort=TRENDING" color="primary">View All</Button>
        </Box>
        {trendingError && <Alert severity="error" sx={{ mb: 2 }}>{trendingError}</Alert>}
        {isLoadingTrending ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}><CircularProgress /></Box>
        ) : trendingOutfits.length === 0 ? (
          <Typography color="text.secondary">No trending outfits available.</Typography>
        ) : (
          <InFeedAdGrid
            pageContext="home"
            targeting={{
              keywords: ['fashion', 'trending', 'popular'],
              categories: ['trending', 'homepage'],
              contextual: { pageType: 'home', fashionStyles: [], brands: [], events: [] }
            }}
            gridItemProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
          >
            {trendingOutfits.map((outfit) => (
              <OutfitCard key={outfit.id} outfit={outfit} />
            ))}
          </InFeedAdGrid>
        )}
      </Box>
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>Browse by Event</Typography>
        {eventsError && <Alert severity="error" sx={{ mb: 2 }}>{eventsError}</Alert>}
        {isLoadingEvents ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}><CircularProgress /></Box>
        ) : Object.keys(eventOutfits).length === 0 ? (
          <Typography color="text.secondary">No event categories available.</Typography>
        ) : (
          <Box>
            {Object.entries(eventOutfits).map(([eventType, outfits]) => (
              <Box key={eventType} sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>{eventType}</Typography>
                  <Button component={RouterLink} to={"/search?eventType=" + encodeURIComponent(eventType)} color="primary">View All</Button>
                </Box>
                <Grid container spacing={3}>
                  {outfits.map((outfit) => (
                    <Grid item xs={12} sm={6} md={3} key={outfit.id}>
                      <OutfitCard outfit={outfit} />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6} md={3}>
                    <NativeInFeedAd
                      index={Object.keys(eventOutfits).indexOf(eventType)}
                      targeting={{
                        keywords: ['fashion', eventType.toLowerCase()],
                        categories: [eventType.toLowerCase(), 'event'],
                        contextual: { pageType: 'home', fashionStyles: [], brands: [], events: [eventType] }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <HowItWorks />
      <FeaturedArticles />
      <PopularCategories />
    </Box>
  );
};

export default HomePage;
